import React, { useState } from 'react';
import { FileIcon, DownloadIcon, ExternalLinkIcon, TrashIcon, FileSymlinkIcon, FolderIcon, CalendarIcon, UserIcon, TagIcon, LinkIcon, FolderTreeIcon, DatabaseIcon, ShieldIcon, ClockIcon, EditIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
export function FileDetails({
  file,
  onClose,
  onDownload,
  onMove,
  formatFileSize,
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'relationships', 'history'
  const [copied, setCopied] = useState(false);
  // Generate a realistic nanoid-like string
  const generateNanoid = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  // Create a deterministic nanoid-like string based on the file ID
  const getNanoid = fileId => {
    // Use the file ID to seed a deterministic but nanoid-like string
    const seed = fileId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      // Use a deterministic approach to generate characters based on the seed
      const charIndex = seed * (i + 1) % chars.length;
      result += chars.charAt(Math.abs(charIndex));
    }
    return result;
  };
  // Generate file URL with the new format and proper nanoid
  const fileUrl = `https://demo.rapidp2p.io/files/${getNanoid(file.id)}`;
  // Handle copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(fileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  // Get file type icon
  const getFileTypeIcon = fileType => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileIcon className="h-5 w-5 text-red-500" data-id="element-5467" />;
      case 'docx':
      case 'doc':
        return <FileIcon className="h-5 w-5 text-blue-500" data-id="element-5468" />;
      case 'xlsx':
      case 'xls':
        return <FileIcon className="h-5 w-5 text-green-500" data-id="element-5469" />;
      case 'pptx':
      case 'ppt':
        return <FileIcon className="h-5 w-5 text-orange-500" data-id="element-5470" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileIcon className="h-5 w-5 text-purple-500" data-id="element-5471" />;
      case 'zip':
      case 'rar':
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-5472" />;
      default:
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-5473" />;
    }
  };
  return <div className="h-full flex flex-col" data-id="element-5474">
      {/* File Header */}
      <div className="border-b border-gray-200 pb-4" data-id="element-5475">
        <div className="flex items-center" data-id="element-5476">
          {getFileTypeIcon(file.type)}
          <h3 className="ml-2 text-lg font-medium text-gray-900 truncate" data-id="element-5477">
            {file.name}
          </h3>
        </div>
        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-5478">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2" data-id="element-5479">
            {file.type.toUpperCase()}
          </span>
          <span data-id="element-5480">{formatFileSize(file.size)}</span>
        </div>
        {/* File URL Section */}
        <div className="mt-3 flex items-center" data-id="element-5481">
          <div className="flex-1 relative" data-id="element-5482">
            <div className="flex items-center bg-gray-50 rounded-md p-2 pr-10 overflow-hidden" data-id="element-5483">
              <LinkIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mr-1.5" data-id="element-5484" />
              <span className="text-xs text-gray-600 truncate" data-id="element-5485">{fileUrl}</span>
            </div>
            <button onClick={copyToClipboard} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600" title="Copy URL" data-id="element-5486">
              {copied ? <CheckIcon className="h-4 w-4 text-green-500" data-id="element-5487" /> : <CopyIcon className="h-4 w-4" data-id="element-5488" />}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b border-gray-200" data-id="element-5489">
        <nav className="-mb-px flex space-x-8" data-id="element-5490">
          <button onClick={() => setActiveTab('details')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5491">
            Details
          </button>
          <button onClick={() => setActiveTab('relationships')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5492">
            Relationships
          </button>
          <button onClick={() => setActiveTab('history')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5493">
            History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4 flex-1 overflow-y-auto" data-id="element-5494">
        {activeTab === 'details' && <div className="space-y-4" data-id="element-5495">
            {/* File Info */}
            <div data-id="element-5496">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5497">
                File Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5498">
                <div className="py-3 flex justify-between text-sm" data-id="element-5499">
                  <dt className="text-gray-500" data-id="element-5500">File Name</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" data-id="element-5501">
                    {file.name}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5502">
                  <dt className="text-gray-500" data-id="element-5503">File Type</dt>
                  <dd className="text-gray-900" data-id="element-5504">{file.type.toUpperCase()}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5505">
                  <dt className="text-gray-500" data-id="element-5506">Size</dt>
                  <dd className="text-gray-900" data-id="element-5507">{formatFileSize(file.size)}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5508">
                  <dt className="text-gray-500" data-id="element-5509">Department</dt>
                  <dd className="text-gray-900" data-id="element-5510">{file.department || 'N/A'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5511">
                  <dt className="text-gray-500" data-id="element-5512">Status</dt>
                  <dd className="text-gray-900" data-id="element-5513">{file.status || 'Active'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5514">
                  <dt className="text-gray-500" data-id="element-5515">File ID</dt>
                  <dd className="text-gray-900 font-mono" data-id="element-5516">{file.id}</dd>
                </div>
              </dl>
            </div>

            {/* Dates */}
            <div data-id="element-5517">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5518">Dates</h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5519">
                <div className="py-3 flex justify-between text-sm" data-id="element-5520">
                  <dt className="text-gray-500" data-id="element-5521">Upload Date</dt>
                  <dd className="text-gray-900" data-id="element-5522">
                    {formatDateTime(file.uploadDate, locale)}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5523">
                  <dt className="text-gray-500" data-id="element-5524">Last Modified</dt>
                  <dd className="text-gray-900" data-id="element-5525">
                    {formatDateTime(file.lastModified, locale)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Tags */}
            {file.tags && file.tags.length > 0 && <div data-id="element-5526">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-5527">Tags</h4>
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-5528">
                  {file.tags.map(tag => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5529">
                      <TagIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5530" />
                      {tag}
                    </span>)}
                </div>
              </div>}

            {/* Storage Information */}
            <div data-id="element-5531">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5532">
                Storage Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5533">
                <div className="py-3 flex justify-between text-sm" data-id="element-5534">
                  <dt className="text-gray-500" data-id="element-5535">Location</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" title={file.storageLocation} data-id="element-5536">
                    {file.storageLocation}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5537">
                  <dt className="text-gray-500" data-id="element-5538">Access Level</dt>
                  <dd className="text-gray-900" data-id="element-5539">{file.accessLevel}</dd>
                </div>
              </dl>
            </div>
          </div>}

        {activeTab === 'relationships' && <div className="space-y-4" data-id="element-5540">
            {/* Parent Files */}
            <div data-id="element-5541">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5542">
                Parent Files
              </h4>
              {file.parentFiles && file.parentFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5543">
                  {file.parentFiles.map(parent => <li key={parent.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5544">
                      <div className="flex items-center" data-id="element-5545">
                        <FolderTreeIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5546" />
                        <span className="text-gray-900" data-id="element-5547">{parent.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5548">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5549" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5550">
                  No parent files
                </p>}
            </div>

            {/* Child Files */}
            <div data-id="element-5551">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5552">Child Files</h4>
              {file.childFiles && file.childFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5553">
                  {file.childFiles.map(child => <li key={child.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5554">
                      <div className="flex items-center" data-id="element-5555">
                        <FileSymlinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5556" />
                        <span className="text-gray-900" data-id="element-5557">{child.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5558">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5559" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5560">
                  No child files
                </p>}
            </div>

            {/* Linked Entities */}
            <div data-id="element-5561">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5562">
                Linked Entities
              </h4>
              {file.linkedEntities && file.linkedEntities.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5563">
                  {file.linkedEntities.map(entity => <li key={entity.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5564">
                      <div className="flex items-center" data-id="element-5565">
                        <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5566" />
                        <span className="text-gray-900" data-id="element-5567">
                          {entity.name}{' '}
                          <span className="text-gray-500" data-id="element-5568">({entity.type})</span>
                        </span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5569">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5570" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5571">
                  No linked entities
                </p>}
            </div>
          </div>}

        {activeTab === 'history' && <div data-id="element-5572">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5573">File History</h4>
            <ul className="mt-2 border-t border-gray-200 divide-y divide-gray-200" data-id="element-5574">
              <li className="py-3" data-id="element-5575">
                <div className="flex items-center text-sm" data-id="element-5576">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5577" />
                  <p className="text-gray-900 font-medium" data-id="element-5578">File modified</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5579">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5580" />
                  <span data-id="element-5581">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5582">•</span>
                  <span data-id="element-5583">{formatDateTime(file.lastModified, locale)}</span>
                </div>
              </li>
              <li className="py-3" data-id="element-5584">
                <div className="flex items-center text-sm" data-id="element-5585">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5586" />
                  <p className="text-gray-900 font-medium" data-id="element-5587">File uploaded</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5588">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5589" />
                  <span data-id="element-5590">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5591">•</span>
                  <span data-id="element-5592">{formatDateTime(file.uploadDate, locale)}</span>
                </div>
              </li>
            </ul>
          </div>}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 border-t border-gray-200 pt-4 flex justify-between" data-id="element-5593">
        <div data-id="element-5594">
          <button onClick={() => onDownload(file.id)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5595">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-5596" />
            Download
          </button>
        </div>
        <div className="flex space-x-2" data-id="element-5597">
          <button onClick={() => onMove(file)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5598">
            <FolderIcon className="h-4 w-4 mr-1" data-id="element-5599" />
            Move
          </button>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5600">
            <EditIcon className="h-4 w-4 mr-1" data-id="element-5601" />
            Edit
          </button>
        </div>
      </div>
    </div>;
}